import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import {
  useLazyGetStudentRegistrationQuery,
  usePostVocationRecordsMutation,
} from './StudentTcDetails.service';

interface Option {
  label: string;
  value: number | string;
}

export interface ExitSurveyPrefill {
  indxId: string | number;
  studId: string;
  name: string;
  fatherName: string;
  schoolName: string;
  udiseNum: string;
  groupName: string;
  exitInterviewDate: string;
  village?: string;
  district?: string | number;
  pincode?: string;
  checked1?: boolean;
  mobileNo?: string;
  parentName?: string;
  parentMobileNo?: string;
  checked2?: boolean;
  selectEduTime?: number | '';
  selectCourseType?: number | '';
  selectStream?: number | '';
  typeOfCourse?: string;
  typeOfStream?: string;
  checked3?: boolean;
  checked4?: boolean;
  checked5?: boolean;
  typeOfEmploye?: number | '';
  PreferredLocation?: number | '';
}

interface Props {
  schoolId: number;
  prefill: ExitSurveyPrefill;
  onProceed: () => void;
  onCancel: () => void;
}

const EDU_TIME: Option[] = [
  { label: 'Full Time', value: 1 },
  { label: 'Part Time', value: 2 },
];
const COURSE_TYPE: Option[] = [
  { label: 'Graduation', value: 1 },
  { label: 'Diploma', value: 2 },
  { label: 'Certification', value: 3 },
  { label: 'Other', value: 4 },
];
const STREAM: Option[] = [
  { label: 'Plain Science', value: 1 },
  { label: 'Engineering Sciences', value: 2 },
  { label: 'Medical Science', value: 3 },
  { label: 'Arts and Humanities', value: 4 },
  { label: 'Commerce', value: 5 },
  { label: 'Other', value: 6 },
];
const EMPLOY_TYPE: Option[] = [
  { label: 'Wage/Salaried Employment', value: 1 },
  { label: 'Self-Employment/Business', value: 2 },
];
const PRE_LOCATION: Option[] = [
  { label: 'Village/City of residence', value: 1 },
  { label: 'Anywhere Within District', value: 2 },
  { label: 'Anywhere Within State', value: 3 },
];

const MOBILE_RE = /^[6-9]\d{9}$/;
const NAME_RE = /^[A-Za-z ]{1,50}$/;
const PIN_RE = /^[6]{1}[0-9]{2}\s{0,1}[0-9]{3}$/;

const StudentExitSurvey: React.FC<Props> = ({
  schoolId,
  prefill,
  onProceed,
  onCancel,
}) => {
  const toast = useRef<Toast>(null);
  const notify = (
    severity: 'success' | 'error' | 'warn' | 'info',
    detail: string
  ): void => toast.current?.show({ severity, detail, life: 4000 });

  const [getRegistration] = useLazyGetStudentRegistrationQuery();
  const [postVocation] = usePostVocationRecordsMutation();

  const [schooldist, setSchooldist] = useState<Option[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const [village, setVillage] = useState(prefill.village ?? '');
  const [district, setDistrict] = useState<number | string | ''>(
    prefill.district ?? ''
  );
  const [pincode, setPincode] = useState(prefill.pincode ?? '');
  const [checked1, setChecked1] = useState(!!prefill.checked1);
  const [mobileNo, setMobileNo] = useState(prefill.mobileNo ?? '');
  const [parentName, setParentName] = useState(prefill.parentName ?? '');
  const [parentMobileNo, setParentMobileNo] = useState(
    prefill.parentMobileNo ?? ''
  );
  const [checked2, setChecked2] = useState(!!prefill.checked2);
  const [selectEduTime, setSelectEduTime] = useState<number | ''>(
    prefill.selectEduTime ?? ''
  );
  const [selectCourseType, setSelectCourseType] = useState<number | ''>(
    prefill.selectCourseType ?? ''
  );
  const [selectStream, setSelectStream] = useState<number | ''>(
    prefill.selectStream ?? ''
  );
  const [typeOfCourse, setTypeOfCourse] = useState(prefill.typeOfCourse ?? '');
  const [typeOfStream, setTypeOfStream] = useState(prefill.typeOfStream ?? '');
  const [checked3, setChecked3] = useState(!!prefill.checked3);
  const [checked4, setChecked4] = useState(!!prefill.checked4);
  const [checked5, setChecked5] = useState(!!prefill.checked5);
  const [typeOfEmploye, setTypeOfEmploye] = useState<number | ''>(
    prefill.typeOfEmploye ?? ''
  );
  const [preferredLocation, setPreferredLocation] = useState<number | ''>(
    prefill.PreferredLocation ?? ''
  );

  // District options come from the studentRegistration master data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRegistration({ schoolId }).unwrap();
        const master = (res.result ?? {}) as Record<string, unknown>;
        const dist = Array.isArray(master.schooldist)
          ? (master.schooldist as Record<string, unknown>[]).map((l) => ({
              label: String(l.district_name ?? ''),
              value: l.id as string | number,
            }))
          : [];
        if (!cancelled) setSchooldist(dist);
      } catch {
        if (!cancelled) setSchooldist([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getRegistration, schoolId]);

  const isValid = (): boolean => {
    if (!village || !district || !parentName || !parentMobileNo) return false;
    if (!NAME_RE.test(parentName)) return false;
    if (!MOBILE_RE.test(parentMobileNo)) return false;
    if (pincode && !PIN_RE.test(pincode)) return false;
    if (checked1 && (!mobileNo || !MOBILE_RE.test(mobileNo))) return false;
    if (checked2) {
      if (!selectEduTime || !selectCourseType || !selectStream) return false;
      if (selectCourseType === 4 && !typeOfCourse) return false;
      if (selectStream === 6 && !typeOfStream) return false;
    }
    if (checked5 && (!typeOfEmploye || !preferredLocation)) return false;
    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitted(true);
    if (!isValid()) {
      notify('warn', 'Please fill all the required fields correctly.');
      return;
    }
    const payload = {
      records: {
        IndxId: prefill.indxId ?? '',
        StudId: prefill.studId,
        SchoolUdiId: schoolId,
        JobRole: prefill.groupName,
        DateExitInter: new Date(),
        Address: village,
        District: district,
        Pincode: pincode,
        PersMblNumYN: checked1 ? '1' : '2',
        PersMblNum: mobileNo,
        FatheOrGurdi: parentName,
        FatherMblNum: parentMobileNo,
        HighStudyYN: checked2 ? '1' : '2',
        CouType: selectEduTime,
        CourseStudy: selectCourseType,
        CourseStudyOthers: typeOfCourse,
        EduStream: selectStream,
        EduStreamOthers: typeOfStream,
        EmpRegYN: checked3 ? '1' : '2',
        NapsYN: checked4 ? '1' : '2',
        JobSelfEmpYN: checked5 ? '1' : '2',
        InterestedIn: typeOfEmploye,
        PrefLoctaion: preferredLocation,
      },
    };
    setSaving(true);
    try {
      const res = await postVocation(payload).unwrap();
      if (res.dataStatus) {
        notify('success', 'Data Saved Successfully');
        onProceed();
      } else {
        notify('error', res.message ?? 'Unable to save the exit survey');
      }
    } catch {
      notify('error', 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const reqErr = (v: unknown): boolean => submitted && !v;

  return (
    <div className="card card-bg-left p-4 student-exit-survey">
      <Toast ref={toast} />
      <h4 className="pop-highlight-text">Exit Survey Form</h4>

      {/* Basic Details (read-only) */}
      <h5 className="pop-highlight-text pt-3">Basic Details</h5>
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Name of the Student</label>
          <InputText className="form-control" value={prefill.name} disabled />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Father&apos;s Name</label>
          <InputText
            className="form-control"
            value={prefill.fatherName}
            disabled
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="inMandatory">School Name</label>
          <InputText
            className="form-control"
            value={prefill.schoolName}
            disabled
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="inMandatory">School UDISE Number</label>
          <InputText
            className="form-control"
            value={prefill.udiseNum}
            disabled
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="inMandatory">Group Name</label>
          <InputText
            className="form-control"
            value={prefill.groupName}
            disabled
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="inMandatory">Date of Exit Survey</label>
          <input
            type="date"
            className="form-control"
            value={prefill.exitInterviewDate}
            disabled
          />
        </div>
      </div>

      {/* Address */}
      <h5 className="pop-highlight-text pt-4">Address</h5>
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Village/City/Town of residence</label>
          <InputText
            className="form-control"
            value={village}
            onChange={(e) => setVillage(e.target.value)}
            placeholder="Enter your village/city/town of residence"
          />
          {reqErr(village) && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">District of Residence</label>
          <Dropdown
            className="w-100"
            options={schooldist}
            value={district || null}
            optionLabel="label"
            optionValue="value"
            showClear
            placeholder="Select District"
            onChange={(e) => setDistrict(e.value)}
          />
          {reqErr(district) && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label>Pincode (If available)</label>
          <InputText
            className="form-control"
            maxLength={6}
            value={pincode}
            onChange={(e) => setPincode(e.target.value)}
            placeholder="Enter your Pincode"
          />
          {submitted && pincode !== '' && !PIN_RE.test(pincode) && (
            <small className="text-danger">It is not a valid pincode.</small>
          )}
        </div>
      </div>

      {/* Contact */}
      <h5 className="pop-highlight-text pt-4">Contact Details</h5>
      <div className="row align-items-center">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <p className="mb-2">Do you have personal mobile number</p>
          <InputSwitch
            checked={checked1}
            onChange={(e) => setChecked1(!!e.value)}
          />
        </div>
        {checked1 && (
          <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
            <label className="inMandatory">Enter Personal Mobile Number</label>
            <InputText
              className="form-control"
              maxLength={10}
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              placeholder="Enter your personal mobile number"
            />
            {submitted && (!mobileNo || !MOBILE_RE.test(mobileNo)) && (
              <small className="text-danger">
                It is not a valid mobile number.
              </small>
            )}
          </div>
        )}
      </div>

      {/* Parent/Guardian contact */}
      <h5 className="pop-highlight-text pt-4">
        Parent/Guardian/Relative&apos;s Contact
      </h5>
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Name</label>
          <InputText
            className="form-control"
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
            placeholder="Enter your parent/guardian name"
          />
          {submitted && (!parentName || !NAME_RE.test(parentName)) && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Mobile Number</label>
          <InputText
            className="form-control"
            maxLength={10}
            value={parentMobileNo}
            onChange={(e) => setParentMobileNo(e.target.value)}
            placeholder="Enter your parent/guardian mobile number"
          />
          {submitted &&
            (!parentMobileNo || !MOBILE_RE.test(parentMobileNo)) && (
              <small className="text-danger">
                It is not a valid mobile number.
              </small>
            )}
        </div>
      </div>

      {/* Education */}
      <h5 className="pop-highlight-text pt-4">Education Details</h5>
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="mb-2 d-block">
            Will you continue your higher studies
          </label>
          <InputSwitch
            checked={checked2}
            onChange={(e) => setChecked2(!!e.value)}
          />
        </div>
      </div>
      {checked2 && (
        <div className="row">
          <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
            <label className="inMandatory">Whether Full Time/Part Time</label>
            <Dropdown
              className="w-100"
              options={EDU_TIME}
              value={selectEduTime || null}
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder="Select Type"
              onChange={(e) => setSelectEduTime(e.value)}
            />
            {reqErr(selectEduTime) && (
              <small className="text-danger">Please fill out this field.</small>
            )}
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
            <label className="inMandatory">Which course will you pursue</label>
            <Dropdown
              className="w-100"
              options={COURSE_TYPE}
              value={selectCourseType || null}
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder="Select Type"
              onChange={(e) => setSelectCourseType(e.value)}
            />
            {reqErr(selectCourseType) && (
              <small className="text-danger">Please fill out this field.</small>
            )}
          </div>
          {selectCourseType === 4 && (
            <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
              <label className="inMandatory">Other</label>
              <InputText
                className="form-control"
                value={typeOfCourse}
                onChange={(e) => setTypeOfCourse(e.target.value)}
              />
              {reqErr(typeOfCourse) && (
                <small className="text-danger">
                  Please fill out this field.
                </small>
              )}
            </div>
          )}
          <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
            <label className="inMandatory">Stream of Education</label>
            <Dropdown
              className="w-100"
              options={STREAM}
              value={selectStream || null}
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder="Select Type"
              onChange={(e) => setSelectStream(e.value)}
            />
            {reqErr(selectStream) && (
              <small className="text-danger">Please fill out this field.</small>
            )}
          </div>
          {selectStream === 6 && (
            <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
              <label className="inMandatory">Other</label>
              <InputText
                className="form-control"
                value={typeOfStream}
                onChange={(e) => setTypeOfStream(e.target.value)}
              />
              {reqErr(typeOfStream) && (
                <small className="text-danger">
                  Please fill out this field.
                </small>
              )}
            </div>
          )}
        </div>
      )}

      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="mb-2 d-block">
            Have you registered on Employment Portal
          </label>
          <InputSwitch
            checked={checked3}
            onChange={(e) => setChecked3(!!e.value)}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="mb-2 d-block">
            Are you willing to get registered on the NAPS portal?
          </label>
          <InputSwitch
            checked={checked4}
            onChange={(e) => setChecked4(!!e.value)}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="mb-2 d-block">
            Are you interested in Job or Self Employment after 12th
          </label>
          <InputSwitch
            checked={checked5}
            onChange={(e) => setChecked5(!!e.value)}
          />
        </div>
      </div>
      {checked5 && (
        <div className="row">
          <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
            <label className="inMandatory">What are you interested in</label>
            <Dropdown
              className="w-100"
              options={EMPLOY_TYPE}
              value={typeOfEmploye || null}
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder="Select Type"
              onChange={(e) => setTypeOfEmploye(e.value)}
            />
            {reqErr(typeOfEmploye) && (
              <small className="text-danger">Please fill out this field.</small>
            )}
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
            <label className="inMandatory">
              Preferred Location for employment
            </label>
            <Dropdown
              className="w-100"
              options={PRE_LOCATION}
              value={preferredLocation || null}
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder="Select Location"
              onChange={(e) => setPreferredLocation(e.value)}
            />
            {reqErr(preferredLocation) && (
              <small className="text-danger">Please fill out this field.</small>
            )}
          </div>
        </div>
      )}

      <div className="text-end mt-4">
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-danger me-2"
          onClick={onCancel}
        />
        <Button
          label="Submit"
          icon="pi pi-check"
          className="p-button-success"
          loading={saving}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default StudentExitSurvey;
